import React from 'react';
import PropTypes from 'prop-types';
import DataTable, { createTheme } from 'react-data-table-component7';

import {
  gray200, gray600, gray900, secondary100, secondary200,
} from 'components/new_ui/Colors';
import LoadingPlaceholder from 'components/new_ui/WaitLayers/LoadingPlaceholder';
import { useTableContext } from './TableContext';
import Actions from './Actions';
import SubHeader from './SubHeader';
import parseColumns from './parseColumns';
import ContextMenu from './ContextMenu';
import DefaultNoData from './NoData';

createTheme('leggup', {
  text: {
    primary: gray900,
    secondary: gray900,
    disabled: gray600,
  },
  context: {
    background: 'none',
  },
  divider: {
    default: gray200,
  },
}, 'light');

const customStyles = (actionsPresent) => ({
  table: {
    style: {
      borderCollapse: 'collapse',
      display: 'table',
      position: 'static',
    },
  },
  tableWrapper: {
    style: {
      display: 'block',
      position: 'static',
    },
  },
  responsiveWrapper: {
    style: {
      position: 'static',
    },
  },
  header: {
    style: {
      padding: 0,
    },
  },
  subHeader: {
    style: {
      display: 'block',
      padding: 0,
    },
  },
  head: {
    style: {
      display: 'table-row-group',
    },
  },
  headRow: {
    style: {
      backgroundColor: secondary200,
      display: 'table-row',
      fontSize: '110%',
      height: '52px',
    },
  },
  headCells: {
    style: {
      display: 'table-cell',
      minWidth: '48px',
      verticalAlign: 'middle',
      ':nth-last-child(2)': {
        paddingRight: actionsPresent ? '56px' : '16px',
      },
      ':last-child': {
        backgroundColor: 'inherit',
        height: actionsPresent ? '52px' : 'auto',
        position: actionsPresent ? 'absolute' : 'static',
        right: actionsPresent ? 0 : 'auto',
      },
    },
  },
  contextMenu: {
    style: {
      padding: 0,
    },
  },
  cells: {
    style: {
      display: 'table-cell',
      paddingBottom: '.5rem',
      paddingTop: '.5rem',
      position: 'static',
      verticalAlign: 'middle',
      ':nth-last-child(2)': {
        paddingRight: actionsPresent ? '56px' : '16px',
      },
    },
  },
  rows: {
    style: {
      display: 'table-row',
      height: '56px',
      position: 'static',
    },
  },
  noData: {
    style: {
      display: 'none',
    },
  },
});

export default function Table(props) {
  const {
    title, columns, data, loading, selectableRows, conditionalRowStyles, rowActions, groupActions, noData,
    afterSelect, selectableRowSelected, selectableRowDisabled, defaultSortFieldId, defaultSortAsc,
    onFilteredDataChange,
    filterOpen,
  } = props;

  const { state, dispatch } = useTableContext();

  const {
    selectedRows,
    searchFilter,
    selectedFilters,
    showOnlySelectedRows,
    resetPaginationToggle,
  } = state;

  const defaultConditionalRowStyles = [
    {
      when: (row) => selectedRows.includes(row.id),
      style: {
        backgroundColor: secondary100,
      },
    },
  ];

  function convertArrayOfObjectsToCSV(array) {
    let result;

    const columnDelimiter = ';';
    const lineDelimiter = '\n';

    const headers = columns.filter((column) => column.exportable)
      .map((column) => column.name);

    result = '';
    result += headers.join(columnDelimiter);
    result += lineDelimiter;

    array.forEach((item) => {
      let ctr = 0;

      columns.filter((column) => column.exportable).forEach((column) => {
        if (ctr > 0) result += columnDelimiter;

        if (column.format) {
          result += column.format(item);
        } else {
          result += column.selector(item);
        }

        ctr += 1;
      });
      result += lineDelimiter;
    });

    return result;
  }

  React.useEffect(() => {
    columns.forEach((column) => {
      const defaultValue = column?.filter?.defaultValue;

      if (defaultValue) {
        Actions.searchBySelect(dispatch, column.name, { value: defaultValue });
      }
    });
  }, []);

  const filteredData = React.useMemo(() => (
    (data || []).filter((item) => columns.every((column) => {
      const selectedValue = selectedFilters[column.name];

      if (!column.filter || !selectedValue?.length) {
        return true;
      }

      const value = column.selector(item)?.toLowerCase();

      if (Array.isArray(selectedValue)) {
        return value && selectedValue.some((val) => value.includes(val));
      }

      return value && value.includes(selectedValue.toLowerCase());
    })).filter((item) => {
      if (!searchFilter) {
        return true;
      }

      return columns.some((column) => {
        const value = column.selector(item);

        return (
          column.searchable && value && value.toLowerCase().includes(searchFilter.toLowerCase())
        );
      });
    }).filter((item) => (showOnlySelectedRows ? selectedRows.includes(item.id) : true))
  ), [data, searchFilter, selectedFilters, showOnlySelectedRows, loading]);

  const onExportClick = () => {
    const link = document.createElement('a');
    let csv = convertArrayOfObjectsToCSV(filteredData);
    if (csv == null) return;

    const filename = 'export.csv';

    if (!csv.match(/^data:text\/csv/i)) {
      csv = `data:text/csv;charset=utf-8,${csv}`;
    }

    link.setAttribute('href', encodeURI(csv));
    link.setAttribute('download', filename);
    link.click();
  };

  React.useEffect(() => {
    Actions.loadFilteredRows(dispatch, filteredData.map((datum) => datum.id));
  }, [filteredData]);

  React.useEffect(() => {
    afterSelect(selectedRows);
  }, [selectedRows]);

  React.useEffect(() => {
    (data || []).forEach((row) => {
      if (selectableRowSelected(row)) {
        Actions.selectRow(dispatch, row, true);
      }

      if (selectableRowDisabled(row)) {
        Actions.disableRow(dispatch, row);
      }
    });
  }, [data]);

  React.useEffect(() => {
    onFilteredDataChange(filteredData);
  }, [filteredData]);

  return (
    <div className="position-relative">
      <div className="position-relative" style={{ zIndex: 1 }}>
        <ContextMenu groupActions={groupActions} />
      </div>

      <DataTable
        title={title}
        columns={parseColumns(columns, selectableRows, selectableRowDisabled, rowActions)}
        defaultSortFieldId={defaultSortFieldId}
        defaultSortAsc={defaultSortAsc}
        data={filteredData}
        persistTableHead
        subHeader
        subHeaderComponent=<SubHeader
          columns={columns}
          selectableRows={selectableRows}
          onExportClick={onExportClick}
          filterOpen={filterOpen}
        />
        noContextMenu
        conditionalRowStyles={defaultConditionalRowStyles.concat(conditionalRowStyles)}
        pagination
        paginationComponentOptions={{ rowsPerPageText: '', selectAllRowsItem: true }}
        paginationResetDefaultPage={resetPaginationToggle}
        theme="leggup"
        customStyles={customStyles(rowActions.length > 0)}
      />

      {loading ? (
        <LoadingPlaceholder text="Loading records..." className="mb-4 py-5" />
      ) : (
        !filteredData.length && noData
      )}
    </div>
  );
}

Table.propTypes = {
  title: PropTypes.string,
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  data: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool,
  selectableRows: PropTypes.bool,
  conditionalRowStyles: PropTypes.arrayOf(PropTypes.object),
  rowActions: PropTypes.arrayOf(PropTypes.object),
  groupActions: PropTypes.arrayOf(PropTypes.object),
  noData: PropTypes.element,
  afterSelect: PropTypes.func,
  selectableRowSelected: PropTypes.func,
  selectableRowDisabled: PropTypes.func,
  defaultSortFieldId: PropTypes.number,
  defaultSortAsc: PropTypes.bool,
  onFilteredDataChange: PropTypes.func,
  filterOpen: PropTypes.bool,
};

Table.defaultProps = {
  title: '',
  data: [],
  loading: false,
  selectableRows: true,
  conditionalRowStyles: [],
  rowActions: [],
  groupActions: [],
  noData: <DefaultNoData />,
  afterSelect: () => {},
  selectableRowSelected: () => {},
  selectableRowDisabled: () => {},
  defaultSortFieldId: undefined,
  defaultSortAsc: true,
  onFilteredDataChange: () => {},
  filterOpen: false,
};
